import AddIcon from '@mui/icons-material/Add';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Pagination from '@mui/material/Pagination';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  ContentCopy,
  Edit,
  Share,
} from '@mui/icons-material';
import {
  Box,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { getHorsePublicShares } from '../../../../../redux/horsePublicShare/actions';
import AddEditModal from './components/AddEditModal';
import CardLoading from './components/CardLoading';

const ShareLinksCardComponent = ({ id }) => {
  const dispatch = useDispatch();

  // Redux state
  const isFetchingHorsePublicShares = useSelector((state) => state.horsePublicShare.isFetchingHorsePublicShares);
  const horsePublicShares = useSelector((state) => state.horsePublicShare.horsePublicShares);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);

  // State
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [selectedHorsePublicShare, setSelectedHorsePublicShare] = React.useState(null);
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'startDate',
      sort: 'desc',
    },
  ]);
  const [openCopied, setOpenCopied] = React.useState(false)

  // const roles = UserService.getUserRolesForTenant(
  //   user,
  //   selectedBarn.tenant,
  // );
  // // eslint-disable-next-line no-unused-vars
  // const isAdmin = isUserAdmin(roles) || isUserOwner(roles);

  React.useEffect(() => {
    dispatch(getHorsePublicShares(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
  }, [id, page]);

  const handleEditClick = (horsePublicShare) => {
    setSelectedHorsePublicShare(horsePublicShare);
    setOpenAddEditModal(true);
  };

  const handleAddClick = () => {
    setSelectedHorsePublicShare(null);
    setOpenAddEditModal(true);
  };

  const handleCloseAddEditModal = () => {
    setSelectedHorsePublicShare(null)
    setOpenAddEditModal(false);
  };

  return (
    <>
      {isFetchingHorsePublicShares
        ? <CardLoading />
        : (
          <Paper
            sx={{
              borderRadius: 3,
              p: 2,
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={2}
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar><Share /></Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Horse Profile Sharing Links
                  </Typography>
                  <Typography sx={{ textWrap: 'auto' }}>
                    Used to share information about this horse with outside individuals for sales, discharges, etc.
                  </Typography>
                </Box>
              </Stack>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddClick}>
                New Share Link
              </Button>
            </Stack>
            <Box>
              <List
                aria-label="horse current location"
                subheader={(
                  <ListSubheader component="div" id="nested-list-subheader-upcoming">
                    Current Sharing Links
                  </ListSubheader>
                )}
              >
                {
                  horsePublicShares.content.length > 0
                    ? horsePublicShares.content.map(((horsePublicShare) => (
                      <ListItem
                        key={horsePublicShare.id}
                        secondaryAction={(
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Tooltip title="Copy Share Link">
                              <IconButton aria-label="copy" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpenCopied(true); navigator.clipboard.writeText(`https://${selectedBarn.tenant}.questri.app/public/horses/${id}/shared/${horsePublicShare.id}`); }}>
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                            <IconButton edge="end" aria-label="edit" onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleEditClick(horsePublicShare); }}>
                              <Edit />
                            </IconButton>
                          </Box>
                        )}
                        disablePadding
                        onClick={() => { handleEditClick(horsePublicShare); }}
                        divider
                      >
                        <ListItemButton>
                          <ListItemIcon><Share /></ListItemIcon>
                          <ListItemText
                            primary={`${horsePublicShare.shareNote || 'Horse Profile Sharing Link'}`}
                            secondary={(
                              <React.Fragment>
                                <Typography
                                  sx={{ display: 'block' }}
                                  component="span"
                                  variant="body2"
                                >
                                  <a target="blank" href={`https://${selectedBarn.tenant}.questri.app/public/horses/${id}/shared/${horsePublicShare.id}`}>{`https://${selectedBarn.tenant}.questri.app/public/horses/${id}/shared/${horsePublicShare.id}`}</a>
                                </Typography>
                                <Typography>
                                  {`Link active ${Moment(horsePublicShare.startDate).format('ll')} ${horsePublicShare.endDate
                                    ? (`through ${Moment(horsePublicShare.endDate).format('ll')}`)
                                    : 'through no end date specified'}`}
                                </Typography>
                              </React.Fragment>
                            )}
                            secondaryTypographyProps={{ sx: { textWrap: 'auto' } }}
                            primaryTypographyProps={{
                              variant: 'h5',
                              maxWidth: '50%',
                              noWrap: true,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )))
                    : (
                      <ListItemButton disabled><ListItemText secondary="No sharing links for this horse" /></ListItemButton>
                    )
                }
              </List>

              {(horsePublicShares.totalPages || 0) > 1 && (
                <Pagination count={horsePublicShares.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingHorsePublicShares} />
              )}
            </Box>
            <Snackbar
              open={openCopied}
              autoHideDuration={3000}
              onClose={() => setOpenCopied(false)}
              message="Sharing Link Copied!"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            />
          </Paper>
        )}
      <AddEditModal
        open={openAddEditModal}
        horsePublicShare={selectedHorsePublicShare}
        horseId={id}
        onClose={handleCloseAddEditModal}
      />
    </>
  );
};

ShareLinksCardComponent.propTypes = { id: PropTypes.string.isRequired };
ShareLinksCardComponent.defaultProps = {};

export default ShareLinksCardComponent;
